/**
 * Computational camouflage effectiveness scoring.
 *
 * Color harmony uses palette histogram matching (not misaligned pixel pairs).
 * Includes distance blur, lighting shift, saliency, scale matching, seasonal drift.
 */

const SAMPLE_SIZE = 128;
/** CSS display size for placement previews */
const PREVIEW_RENDER_SIZE = 480;
/** Export at 2× for sharp PNGs on retina displays */
const PREVIEW_EXPORT_DPR = 2;
const CSI_DELTA_E_MAX = 18;
const PLACEMENT_BLEND_ALPHA = 0.72;
const MONTE_CARLO_TRIALS = 14;

export const PLACEMENT_TRIAL_COUNT = MONTE_CARLO_TRIALS;

const METRIC_WEIGHTS = {
  colorHarmony: 0.14,
  textureMatch: 0.08,
  luminanceBalance: 0.06,
  outlineDisruption: 0.08,
  placementRobustness: 0.12,
  distanceSimulation: 0.14,
  lightingRobustness: 0.12,
  saliencyStealth: 0.12,
  scaleMatching: 0.08,
  seasonalDrift: 0.06,
};

const METRIC_META = {
  colorHarmony: {
    label: 'Color Harmony',
    shortLabel: 'Color',
    description:
      'Palette overlap in CIELAB space — compares color distributions, not random pixel pairs. Sky regions de-emphasized.',
    icon: '🎨',
  },
  textureMatch: {
    label: 'Texture Match',
    shortLabel: 'Texture',
    description: 'Gradient grain alignment between terrain and pattern.',
    icon: '🌿',
  },
  luminanceBalance: {
    label: 'Light & Shadow',
    shortLabel: 'Light',
    description: 'Brightness range match between terrain and pattern.',
    icon: '☀️',
  },
  outlineDisruption: {
    label: 'Outline Disruption',
    shortLabel: 'Disruption',
    description: 'Multi-scale edge orientation alignment between terrain and pattern for silhouette breakup.',
    icon: '〰️',
  },
  placementRobustness: {
    label: 'Placement Robustness',
    shortLabel: 'Placement',
    description: 'Consistency across random overlay positions and sizes.',
    icon: '📍',
  },
  distanceSimulation: {
    label: 'Distance Simulation',
    shortLabel: 'Distance',
    description: 'Color match holds when scene and pattern are blurred (mid-range and far viewing distances).',
    icon: '🔭',
  },
  lightingRobustness: {
    label: 'Lighting Robustness',
    shortLabel: 'Lighting',
    description: 'Palette still fits under brighter, dimmer, warm, and cool lighting shifts.',
    icon: '💡',
  },
  saliencyStealth: {
    label: 'Saliency Stealth',
    shortLabel: 'Saliency',
    description:
      'Multi-placement check that the overlay preserves local visual saliency vs bare ground and does not pop out from surroundings.',
    icon: '👁️',
  },
  scaleMatching: {
    label: 'Scale Matching',
    shortLabel: 'Scale',
    description: 'Pattern texture scale aligns with environment detail at near, mid, and far scales.',
    icon: '📐',
  },
  seasonalDrift: {
    label: 'Seasonal Drift',
    shortLabel: 'Seasonal',
    description: 'Palette remains compatible under spring, fall, and winter color shifts.',
    icon: '🍂',
  },
};

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function scoreFromSimilarity(similarity) {
  return Math.round(clamp(similarity, 0, 1) * 100);
}

function mulberry32(seed) {
  let state = seed | 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), state | 1);
    t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function rgbToLab(r, g, b) {
  let rr = r / 255;
  let gg = g / 255;
  let bb = b / 255;

  rr = rr > 0.04045 ? ((rr + 0.055) / 1.055) ** 2.4 : rr / 12.92;
  gg = gg > 0.04045 ? ((gg + 0.055) / 1.055) ** 2.4 : gg / 12.92;
  bb = bb > 0.04045 ? ((bb + 0.055) / 1.055) ** 2.4 : bb / 12.92;

  let x = (rr * 0.4124564 + gg * 0.3575761 + bb * 0.1804375) / 0.95047;
  let y = (rr * 0.2126729 + gg * 0.7151522 + bb * 0.0721750) / 1.0;
  let z = (rr * 0.0193339 + gg * 0.1191920 + bb * 0.9503041) / 1.08883;

  const f = (t) => (t > 0.008856 ? t ** (1 / 3) : 7.787 * t + 16 / 116);

  x = f(x);
  y = f(y);
  z = f(z);

  return { L: 116 * y - 16, a: 500 * (x - y), b: 200 * (y - z) };
}

function labToRgb(lab) {
  let y = (lab.L + 16) / 116;
  let x = lab.a / 500 + y;
  let z = y - lab.b / 200;

  const finv = (t) => {
    const t3 = t ** 3;
    return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787;
  };

  x = 0.95047 * finv(x);
  y = 1.0 * finv(y);
  z = 1.08883 * finv(z);

  let r = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
  let g = x * -0.969266 + y * 1.8760108 + z * 0.041556;
  let b = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;

  const gamma = (c) => (c > 0.0031308 ? 1.055 * c ** (1 / 2.4) - 0.055 : 12.92 * c);

  return {
    r: clamp(Math.round(gamma(r) * 255), 0, 255),
    g: clamp(Math.round(gamma(g) * 255), 0, 255),
    b: clamp(Math.round(gamma(b) * 255), 0, 255),
  };
}

function deltaE(lab1, lab2) {
  const dL = lab1.L - lab2.L;
  const da = lab1.a - lab2.a;
  const db = lab1.b - lab2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

function sampleImageData(img, size = SAMPLE_SIZE) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, size, size);
  return ctx.getImageData(0, 0, size, size);
}

function blurImageData(imageData, radiusPx) {
  if (radiusPx <= 0) {
    return new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
  }

  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const src = document.createElement('canvas');
  src.width = imageData.width;
  src.height = imageData.height;
  src.getContext('2d').putImageData(imageData, 0, 0);

  const ctx = canvas.getContext('2d');
  ctx.filter = `blur(${radiusPx}px)`;
  ctx.drawImage(src, 0, 0);
  ctx.filter = 'none';
  return ctx.getImageData(0, 0, imageData.width, imageData.height);
}

function computeRegionWeights(imageData) {
  const { data, width, height } = imageData;
  const weights = new Float32Array(width * height);

  for (let y = 0; y < height; y++) {
    const verticalPos = y / height;
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const lab = rgbToLab(r, g, b);

      const isBlueSky = verticalPos < 0.42 && lab.L > 42 && b > r + 8 && lab.b < 8;
      const isBrightUpperSky = verticalPos < 0.3 && lab.L > 72;
      const isCloudWhite = verticalPos < 0.38 && lab.L > 82 && Math.abs(lab.a) < 6;
      const greenVegetation = g >= r * 0.85 && lab.a < 15 && lab.L > 18 && lab.L < 75;
      const earthTone = lab.a > 2 && lab.b > 2 && lab.L < 68;
      const isLowerTerrain = verticalPos > 0.35;

      let weight = 0.45;
      if (isBlueSky || isBrightUpperSky || isCloudWhite) weight = 0.04;
      else if (isLowerTerrain && (greenVegetation || earthTone)) weight = 1.0;
      else if (isLowerTerrain) weight = 0.82;
      else if (verticalPos < 0.25) weight = 0.12;

      weights[y * width + x] = weight;
    }
  }

  return weights;
}

function extractLabPixels(imageData) {
  const { data } = imageData;
  const pixels = [];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    pixels.push(rgbToLab(data[i], data[i + 1], data[i + 2]));
  }
  return pixels;
}

function collectWeightedLabPixels(imageData, weights, minWeight = 0.35) {
  const { data, width, height } = imageData;
  const pixels = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const w = weights[y * width + x];
      if (w < minWeight) continue;
      const idx = (y * width + x) * 4;
      if (data[idx + 3] < 128) continue;
      pixels.push(rgbToLab(data[idx], data[idx + 1], data[idx + 2]));
    }
  }

  return pixels;
}

function buildHistogram(values, binCount, minVal, maxVal) {
  const bins = new Float32Array(binCount);
  const range = maxVal - minVal || 1;
  for (const value of values) {
    const bin = Math.min(binCount - 1, Math.floor(((value - minVal) / range) * binCount));
    bins[bin] += 1;
  }
  const total = values.length || 1;
  for (let i = 0; i < binCount; i++) bins[i] /= total;
  return bins;
}

function build3DLabHistogram(pixels, binsPerAxis = 6) {
  const size = binsPerAxis ** 3;
  const bins = new Float32Array(size);

  for (const p of pixels) {
    const lBin = clamp(Math.floor((p.L / 100) * binsPerAxis), 0, binsPerAxis - 1);
    const aBin = clamp(Math.floor(((p.a + 128) / 256) * binsPerAxis), 0, binsPerAxis - 1);
    const bBin = clamp(Math.floor(((p.b + 128) / 256) * binsPerAxis), 0, binsPerAxis - 1);
    bins[lBin * binsPerAxis * binsPerAxis + aBin * binsPerAxis + bBin] += 1;
  }

  const total = pixels.length || 1;
  for (let i = 0; i < size; i++) bins[i] /= total;
  return bins;
}

function histogramIntersection(h1, h2) {
  let intersection = 0;
  for (let i = 0; i < h1.length; i++) intersection += Math.min(h1[i], h2[i]);
  return intersection;
}

/** Nearest-palette: each pattern color's closest match in environment palette */
function scorePaletteCoverage(envPixels, patternPixels) {
  if (envPixels.length === 0 || patternPixels.length === 0) return 0;

  const envSample = envPixels.length > 500
    ? envPixels.filter((_, i) => i % Math.ceil(envPixels.length / 500) === 0)
    : envPixels;
  const patSample = patternPixels.filter((_, i) => i % 2 === 0);

  let totalMinDe = 0;
  for (const p of patSample) {
    let minDe = Infinity;
    for (const e of envSample) {
      const de = deltaE(p, e);
      if (de < minDe) minDe = de;
    }
    totalMinDe += minDe;
  }

  const avgMinDe = totalMinDe / patSample.length;
  return clamp(1 - avgMinDe / CSI_DELTA_E_MAX, 0, 1);
}

/**
 * Fixed color harmony: histogram intersection + palette coverage.
 * (Previous version wrongly paired unrelated pixels by index.)
 */
function scoreColorHarmony(envPixels, patternPixels) {
  if (envPixels.length === 0 || patternPixels.length === 0) return 0;

  const histScore = histogramIntersection(
    build3DLabHistogram(envPixels),
    build3DLabHistogram(patternPixels)
  );
  const paletteScore = scorePaletteCoverage(envPixels, patternPixels);

  return scoreFromSimilarity(histScore * 0.55 + paletteScore * 0.45);
}

function toGrayscale(imageData) {
  const { data, width, height } = imageData;
  const gray = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return { gray, width, height };
}

function computeGradientMagnitudes(gray, width, height) {
  const magnitudes = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx =
        -gray[idx - width - 1] - 2 * gray[idx - 1] - gray[idx + width - 1] +
        gray[idx - width + 1] + 2 * gray[idx + 1] + gray[idx + width + 1];
      const gy =
        -gray[idx - width - 1] - 2 * gray[idx - width] - gray[idx - width + 1] +
        gray[idx + width - 1] + 2 * gray[idx + width] + gray[idx + width + 1];
      magnitudes[idx] = Math.sqrt(gx * gx + gy * gy);
    }
  }
  return magnitudes;
}

function boxBlur1D(src, width, height, radius) {
  const out = new Float32Array(src.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      let count = 0;
      for (let k = -radius; k <= radius; k++) {
        const xx = clamp(x + k, 0, width - 1);
        sum += src[y * width + xx];
        count += 1;
      }
      out[y * width + x] = sum / count;
    }
  }
  return out;
}

function boxBlurGray(gray, width, height, radius) {
  const h = boxBlur1D(gray, width, height, radius);
  const t = new Float32Array(h.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) t[x * height + y] = h[y * width + x];
  }
  const tb = boxBlur1D(t, height, width, radius);
  const out = new Float32Array(gray.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) out[y * width + x] = tb[x * height + y];
  }
  return out;
}

function scoreTextureMatch(envData, patternData, regionWeights) {
  const envGray = toGrayscale(envData);
  const patternGray = toGrayscale(patternData);
  const { width, height } = envData;
  const envGrad = computeGradientMagnitudes(envGray.gray, width, height);
  const patternGrad = computeGradientMagnitudes(patternGray.gray, patternGray.width, patternGray.height);

  const envValues = [];
  const patternValues = Array.from(patternGrad).filter((v) => v > 0);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (regionWeights[y * width + x] < 0.3) continue;
      const v = envGrad[y * width + x];
      if (v > 0) envValues.push(v);
    }
  }

  if (envValues.length === 0 || patternValues.length === 0) return 50;
  const maxGrad = Math.max(...envValues, ...patternValues, 1);
  return scoreFromSimilarity(
    histogramIntersection(buildHistogram(envValues, 16, 0, maxGrad), buildHistogram(patternValues, 16, 0, maxGrad))
  );
}

function scoreLuminanceBalance(envPixels, patternPixels) {
  return scoreFromSimilarity(
    histogramIntersection(
      buildHistogram(envPixels.map((p) => p.L), 12, 0, 100),
      buildHistogram(patternPixels.map((p) => p.L), 12, 0, 100)
    )
  );
}

function downsampleGray(gray, width, height, targetW, targetH) {
  const scaled = new Float32Array(targetW * targetH);
  for (let y = 0; y < targetH; y++) {
    for (let x = 0; x < targetW; x++) {
      scaled[y * targetW + x] = gray[Math.min(height - 1, Math.floor((y / targetH) * height)) * width +
        Math.min(width - 1, Math.floor((x / targetW) * width))];
    }
  }
  return scaled;
}

function computeOrientationHistogram(gray, width, height, bins = 8) {
  const histogram = new Float32Array(bins);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx =
        -gray[idx - width - 1] - 2 * gray[idx - 1] - gray[idx + width - 1] +
        gray[idx - width + 1] + 2 * gray[idx + 1] + gray[idx + width + 1];
      const gy =
        -gray[idx - width - 1] - 2 * gray[idx - width] - gray[idx - width + 1] +
        gray[idx + width - 1] + 2 * gray[idx + width] + gray[idx + width + 1];
      const mag = Math.sqrt(gx * gx + gy * gy);
      if (mag < 3) continue;
      const angle = Math.atan2(gy, gx) + Math.PI;
      const bin = Math.min(bins - 1, Math.floor((angle / (2 * Math.PI)) * bins));
      histogram[bin] += mag;
    }
  }
  const total = histogram.reduce((sum, v) => sum + v, 0) || 1;
  for (let i = 0; i < bins; i++) histogram[i] /= total;
  return histogram;
}

function meanGradientInRegion(grad, width, height, regionWeights, minWeight = 0.3) {
  let sum = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      if (regionWeights && regionWeights[y * width + x] < minWeight) continue;
      const v = grad[y * width + x];
      if (v > 0) {
        sum += v;
        count += 1;
      }
    }
  }
  return count > 0 ? sum / count : 0;
}

function scoreOutlineDisruption(envData, patternData, regionWeights) {
  const envGray = toGrayscale(envData);
  const patGray = toGrayscale(patternData);
  const { width, height } = envData;
  const scales = [1, 2, 4];
  let orientationMatchSum = 0;

  for (const scale of scales) {
    const sw = Math.max(16, Math.floor(width / scale));
    const sh = Math.max(16, Math.floor(height / scale));
    const envScaled = downsampleGray(envGray.gray, width, height, sw, sh);
    const patScaled = downsampleGray(patGray.gray, patGray.width, patGray.height, sw, sh);
    const envOri = computeOrientationHistogram(envScaled, sw, sh);
    const patOri = computeOrientationHistogram(patScaled, sw, sh);
    orientationMatchSum += histogramIntersection(envOri, patOri);
  }

  const orientationMatch = orientationMatchSum / scales.length;
  const envGrad = computeGradientMagnitudes(envGray.gray, width, height);
  const patGrad = computeGradientMagnitudes(patGray.gray, patGray.width, patGray.height);
  const envMean = meanGradientInRegion(envGrad, width, height, regionWeights);
  const patMean = meanGradientInRegion(patGrad, patGray.width, patGray.height, null, 0);

  let energyScore = 0.5;
  if (envMean > 0 && patMean > 0) {
    const ratio = patMean / envMean;
    energyScore = clamp(1 - Math.abs(Math.log(ratio)) / Math.log(2.2), 0, 1);
  }

  return scoreFromSimilarity(orientationMatch * 0.65 + energyScore * 0.35);
}

function generatePlacements(width, height, regionWeights, rng) {
  const placements = [];
  for (let attempt = 0; attempt < MONTE_CARLO_TRIALS * 3 && placements.length < MONTE_CARLO_TRIALS; attempt++) {
    // Random square overlay — 6%–45% of frame width
    const tileW = Math.max(8, Math.floor(width * (0.06 + rng() * 0.39)));
    const tileH = tileW;
    const startX = Math.floor(rng() * Math.max(1, width - tileW));
    const startY = Math.floor(rng() * Math.max(1, height - tileH));

    let weightSum = 0;
    let count = 0;
    for (let y = startY; y < startY + tileH; y++) {
      for (let x = startX; x < startX + tileW; x++) {
        weightSum += regionWeights[y * width + x];
        count += 1;
      }
    }
    if (count > 0 && weightSum / count >= 0.25) {
      placements.push({ startX, startY, tileW, tileH, avgWeight: weightSum / count });
    }
  }

  while (placements.length < MONTE_CARLO_TRIALS) {
    const tileW = Math.floor(width * 0.22);
    placements.push({ startX: Math.floor((width - tileW) / 2), startY: Math.floor(height * 0.45), tileW, tileH: tileW, avgWeight: 0.8 });
  }
  return placements.slice(0, MONTE_CARLO_TRIALS);
}

function samplePatternPixel(patternData, x, y, tileW, tileH) {
  const patX = clamp(Math.floor((x / tileW) * patternData.width), 0, patternData.width - 1);
  const patY = clamp(Math.floor((y / tileH) * patternData.height), 0, patternData.height - 1);
  const patIdx = (patY * patternData.width + patX) * 4;
  return {
    r: patternData.data[patIdx],
    g: patternData.data[patIdx + 1],
    b: patternData.data[patIdx + 2],
  };
}

function scoreSinglePlacement(envData, patternData, placement) {
  const { width, height } = envData;
  const { startX, startY, tileW, tileH } = placement;
  const alpha = PLACEMENT_BLEND_ALPHA;
  let interiorMatch = 0;
  let interiorCount = 0;
  let borderContrast = 0;
  let borderCount = 0;

  for (let y = 0; y < tileH; y++) {
    for (let x = 0; x < tileW; x++) {
      const px = startX + x;
      const py = startY + y;
      if (px >= width || py >= height) continue;

      const envIdx = (py * width + px) * 4;
      const pat = samplePatternPixel(patternData, x, y, tileW, tileH);
      const blended = {
        r: envData.data[envIdx] * (1 - alpha) + pat.r * alpha,
        g: envData.data[envIdx + 1] * (1 - alpha) + pat.g * alpha,
        b: envData.data[envIdx + 2] * (1 - alpha) + pat.b * alpha,
      };
      const envLab = rgbToLab(envData.data[envIdx], envData.data[envIdx + 1], envData.data[envIdx + 2]);
      const blendLab = rgbToLab(blended.r, blended.g, blended.b);
      interiorMatch += 1 - clamp(deltaE(envLab, blendLab) / CSI_DELTA_E_MAX, 0, 1);
      interiorCount += 1;

      if (x === 0 || x === tileW - 1 || y === 0 || y === tileH - 1) {
        borderContrast += deltaE(blendLab, envLab);
        borderCount += 1;
      }
    }
  }

  const matchScore = interiorCount > 0 ? interiorMatch / interiorCount : 0;
  const edgeScore = 1 - clamp((borderCount > 0 ? borderContrast / borderCount : CSI_DELTA_E_MAX) / CSI_DELTA_E_MAX, 0, 1) * 0.85;
  const combined = matchScore * 0.55 + edgeScore * 0.45;
  return scoreFromSimilarity(combined);
}

function scorePlacementRobustness(envData, patternData, placements) {
  const trialScores = placements.map((p) => ({ score: scoreSinglePlacement(envData, patternData, p), placement: p }));
  trialScores.sort((a, b) => a.score - b.score);
  const scores = trialScores.map((t) => t.score);
  const median = scores[Math.floor(scores.length / 2)];
  const worstCase = scores[Math.floor(scores.length * 0.15)];
  const bestCase = scores[Math.floor(scores.length * 0.85)];

  return {
    score: clamp(Math.round(median * 0.35 + worstCase * 0.45 + bestCase * 0.2), 0, 100),
    median,
    worstCase,
    bestCase,
    trialCount: scores.length,
    allTrials: trialScores,
    samples: [trialScores[0], trialScores[Math.floor(trialScores.length / 2)], trialScores[trialScores.length - 1]],
  };
}

function scoreDistanceSimulation(envData, patternData, regionWeights) {
  const blurLevels = [
    { radius: 0, label: 'near' },
    { radius: 2.5, label: 'mid' },
    { radius: 5, label: 'far' },
  ];

  const scores = blurLevels.map(({ radius }) => {
    const blurredEnv = blurImageData(envData, radius);
    const blurredPat = blurImageData(patternData, radius);
    const envPx = collectWeightedLabPixels(blurredEnv, regionWeights);
    const patPx = extractLabPixels(blurredPat);
    return scoreColorHarmony(envPx, patPx);
  });

  return {
    score: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
    breakdown: blurLevels.map((l, i) => ({ ...l, score: scores[i] })),
  };
}

function applyLightingShiftToPixels(pixels, { brightness = 1, warm = 0, cool = 0 }) {
  return pixels.map((p) => {
    let lab = { L: p.L * brightness, a: p.a + warm * 8 - cool * 4, b: p.b - warm * 4 + cool * 6 };
    lab.L = clamp(lab.L, 0, 100);
    return lab;
  });
}

function scoreLightingRobustness(envPixels, patternPixels) {
  const shifts = [
    { brightness: 1.18, warm: 0.3, cool: 0, label: 'bright sun' },
    { brightness: 0.82, warm: 0, cool: 0, label: 'overcast' },
    { brightness: 1, warm: 0.8, cool: 0, label: 'warm light' },
    { brightness: 0.95, warm: 0, cool: 0.9, label: 'cool shade' },
  ];

  const scores = shifts.map((shift) => {
    const shiftedEnv = applyLightingShiftToPixels(envPixels, shift);
    return scoreColorHarmony(shiftedEnv, patternPixels);
  });

  return {
    score: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
    breakdown: shifts.map((s, i) => ({ label: s.label, score: scores[i] })),
  };
}

function applySeasonalShift(pixels, season) {
  return pixels.map((p) => {
    switch (season) {
      case 'spring':
        return { L: clamp(p.L * 1.05, 0, 100), a: p.a - 2, b: p.b + 3 };
      case 'fall':
        return { L: clamp(p.L * 0.95, 0, 100), a: p.a + 6, b: p.b + 4 };
      case 'winter':
        return { L: clamp(p.L * 0.88, 0, 100), a: p.a - 3, b: p.b - 5 };
      default:
        return p;
    }
  });
}

function scoreSeasonalDrift(envPixels, patternPixels) {
  const seasons = ['spring', 'fall', 'winter'];
  const scores = seasons.map((season) =>
    scoreColorHarmony(applySeasonalShift(envPixels, season), patternPixels)
  );

  return {
    score: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
    breakdown: seasons.map((s, i) => ({ label: s, score: scores[i] })),
  };
}

function computeScaleProfile(gray, width, height, regionWeights, isPattern) {
  const scales = [1, 2, 4];
  const profile = [];

  for (const scale of scales) {
    const sw = Math.max(8, Math.floor(width / scale));
    const sh = Math.max(8, Math.floor(height / scale));
    const scaled = new Float32Array(sw * sh);

    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        scaled[y * sw + x] = gray[Math.min(height - 1, Math.floor((y / sh) * height)) * width +
          Math.min(width - 1, Math.floor((x / sw) * width))];
      }
    }

    const grad = computeGradientMagnitudes(scaled, sw, sh);
    let sum = 0;
    let count = 0;

    if (isPattern) {
      for (const v of grad) {
        if (v > 0) { sum += v; count += 1; }
      }
    } else {
      for (let y = 0; y < sh; y++) {
        for (let x = 0; x < sw; x++) {
          const origY = Math.min(height - 1, Math.floor((y / sh) * height));
          const origX = Math.min(width - 1, Math.floor((x / sw) * width));
          if (regionWeights[origY * width + origX] < 0.35) continue;
          const v = grad[y * sw + x];
          if (v > 0) { sum += v; count += 1; }
        }
      }
    }

    profile.push(count > 0 ? sum / count : 0);
  }

  return profile;
}

function scoreScaleMatching(envData, patternData, regionWeights) {
  const envGray = toGrayscale(envData);
  const patGray = toGrayscale(patternData);
  const envProfile = computeScaleProfile(envGray.gray, envGray.width, envGray.height, regionWeights, false);
  const patProfile = computeScaleProfile(patGray.gray, patGray.width, patGray.height, regionWeights, true);

  const envMax = Math.max(...envProfile, 1);
  const patMax = Math.max(...patProfile, 1);
  const envNorm = envProfile.map((v) => v / envMax);
  const patNorm = patProfile.map((v) => v / patMax);

  let similarity = 0;
  for (let i = 0; i < envNorm.length; i++) {
    similarity += 1 - Math.abs(envNorm[i] - patNorm[i]);
  }
  return scoreFromSimilarity(similarity / envNorm.length);
}

function isInsidePlacement(x, y, placement) {
  const { startX, startY, tileW, tileH } = placement;
  return x >= startX && x < startX + tileW && y >= startY && y < startY + tileH;
}

function buildPlacementCompositeImageData(envData, patternData, placement, alpha = PLACEMENT_BLEND_ALPHA) {
  const { width, height } = envData;
  const data = new Uint8ClampedArray(envData.data);
  const { startX, startY, tileW, tileH } = placement;

  for (let y = 0; y < tileH; y++) {
    for (let x = 0; x < tileW; x++) {
      const px = startX + x;
      const py = startY + y;
      if (px >= width || py >= height) continue;

      const envIdx = (py * width + px) * 4;
      const pat = samplePatternPixel(patternData, x, y, tileW, tileH);
      data[envIdx] = envData.data[envIdx] * (1 - alpha) + pat.r * alpha;
      data[envIdx + 1] = envData.data[envIdx + 1] * (1 - alpha) + pat.g * alpha;
      data[envIdx + 2] = envData.data[envIdx + 2] * (1 - alpha) + pat.b * alpha;
    }
  }

  return new ImageData(data, width, height);
}

function collectSurroundRingIndices(placement, width, height, margin = 3) {
  const { startX, startY, tileW, tileH } = placement;
  const ringMargin = Math.max(margin, Math.floor(Math.min(tileW, tileH) * 0.12));
  const indices = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const inRing =
        x >= startX - ringMargin && x < startX + tileW + ringMargin
        && y >= startY - ringMargin && y < startY + tileH + ringMargin;
      if (!isInsidePlacement(x, y, placement) && inRing) {
        indices.push(y * width + x);
      }
    }
  }

  return indices;
}

/**
 * Itti-Koch-style center-surround contrast on luminance (local, not global FFT).
 */
function computeCenterSurroundSaliency(gray, width, height) {
  const scales = [1, 2, 4];
  const combined = new Float32Array(width * height);

  for (const radius of scales) {
    const center = boxBlurGray(gray, width, height, Math.max(1, radius));
    const surround = boxBlurGray(gray, width, height, Math.max(2, radius * 3));
    for (let i = 0; i < gray.length; i++) {
      combined[i] += Math.abs(center[i] - surround[i]);
    }
  }

  for (let i = 0; i < combined.length; i++) {
    combined[i] /= scales.length;
  }

  return combined;
}

/**
 * Per-placement stealth: does the overlay preserve local saliency vs bare ground
 * and stay visually quiet relative to the immediate surround?
 */
function scoreSinglePlacementStealth(envData, patternData, placement) {
  const composite = buildPlacementCompositeImageData(envData, patternData, placement);
  const { width, height } = envData;
  const { gray: envGray } = toGrayscale(envData);
  const { gray: blendGray } = toGrayscale(composite);
  const envSal = computeCenterSurroundSaliency(envGray, width, height);
  const blendSal = computeCenterSurroundSaliency(blendGray, width, height);

  let absDiffSum = 0;
  let excessSum = 0;
  let patchBlendSum = 0;
  let patchEnvSum = 0;
  let patchCount = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!isInsidePlacement(x, y, placement)) continue;
      const i = y * width + x;
      absDiffSum += Math.abs(blendSal[i] - envSal[i]);
      excessSum += Math.max(0, blendSal[i] - envSal[i]);
      patchBlendSum += blendSal[i];
      patchEnvSum += envSal[i];
      patchCount += 1;
    }
  }

  if (patchCount === 0) return 0;

  const ringIndices = collectSurroundRingIndices(placement, width, height);
  let ringBlendSum = 0;
  for (const i of ringIndices) ringBlendSum += blendSal[i];
  const ringBlendMean = ringIndices.length > 0 ? ringBlendSum / ringIndices.length : patchEnvSum / patchCount;

  const ref = Math.max(patchEnvSum / patchCount, 1e-6);
  const fidelity = clamp(1 - (absDiffSum / patchCount) / (ref * 1.15), 0, 1);
  const gainStealth = clamp(1 - (excessSum / patchCount) / (ref * 0.9), 0, 1);
  const popRatio = (patchBlendSum / patchCount) / Math.max(ringBlendMean, 1e-6);
  const popStealth = clamp(1.25 - popRatio, 0, 1) / 1.25;

  return scoreFromSimilarity(fidelity * 0.45 + gainStealth * 0.35 + popStealth * 0.2);
}

function scoreSaliencyStealth(envData, patternData, placements) {
  const trialScores = placements.map((placement) =>
    scoreSinglePlacementStealth(envData, patternData, placement)
  );
  trialScores.sort((a, b) => a - b);

  const median = trialScores[Math.floor(trialScores.length / 2)];
  const worstCase = trialScores[Math.floor(trialScores.length * 0.15)];
  const bestCase = trialScores[Math.floor(trialScores.length * 0.85)];

  return clamp(Math.round(median * 0.35 + worstCase * 0.45 + bestCase * 0.2), 0, 100);
}

function canvasToPreviewUrl(canvas) {
  return canvas.toDataURL('image/png');
}

function createPreviewCanvas(logicalSize) {
  const dpr = PREVIEW_EXPORT_DPR;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(logicalSize * dpr);
  canvas.height = Math.round(logicalSize * dpr);
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return { canvas, ctx, logicalSize };
}

/**
 * Draw environment + single scaled pattern swatch for the placement region.
 */
function drawPlacementOnCanvas(ctx, envImg, patternImg, placement, logicalSize) {
  const scale = logicalSize / SAMPLE_SIZE;

  ctx.drawImage(envImg, 0, 0, logicalSize, logicalSize);

  const dx = placement.startX * scale;
  const dy = placement.startY * scale;
  const dw = placement.tileW * scale;
  const dh = placement.tileH * scale;

  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.drawImage(patternImg, dx, dy, dw, dh);
  ctx.restore();
}

function rasterizeEnvironmentOnly(envImg, logicalSize = SAMPLE_SIZE) {
  const canvas = document.createElement('canvas');
  canvas.width = logicalSize;
  canvas.height = logicalSize;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(envImg, 0, 0, logicalSize, logicalSize);
  return ctx.getImageData(0, 0, logicalSize, logicalSize);
}

function rasterizePlacementComposite(envImg, patternImg, placement, logicalSize = SAMPLE_SIZE) {
  const canvas = document.createElement('canvas');
  canvas.width = logicalSize;
  canvas.height = logicalSize;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  drawPlacementOnCanvas(ctx, envImg, patternImg, placement, logicalSize);
  return ctx.getImageData(0, 0, logicalSize, logicalSize);
}

function renderPlacementPreview(envImg, patternImg, placement, logicalSize = PREVIEW_RENDER_SIZE) {
  const { canvas, ctx } = createPreviewCanvas(logicalSize);
  drawPlacementOnCanvas(ctx, envImg, patternImg, placement, logicalSize);
  return canvasToPreviewUrl(canvas);
}

function createSharpThumbnail(img, logicalSize = PREVIEW_RENDER_SIZE) {
  const { canvas, ctx } = createPreviewCanvas(logicalSize);
  ctx.drawImage(img, 0, 0, logicalSize, logicalSize);
  return canvasToPreviewUrl(canvas);
}

function createPlacementPreviewCollage(envImg, patternImg, samples, logicalSize = PREVIEW_RENDER_SIZE) {
  const sample = samples[Math.floor(samples.length / 2)];
  return renderPlacementPreview(envImg, patternImg, sample.placement, logicalSize);
}

function createAllPlacementPreviews(envImg, patternImg, envData, patternData, trialScores) {
  const mid = Math.floor(trialScores.length / 2);
  const last = trialScores.length - 1;

  return trialScores.map((sample, i) => ({
    index: i + 1,
    total: trialScores.length,
    score: Math.round(sample.score),
    tag: i === 0 ? 'Worst' : i === mid ? 'Median' : i === last ? 'Best' : null,
    url: renderPlacementPreview(envImg, patternImg, sample.placement),
  }));
}

function createMultiPlacementPreviews(envImg, patternImg, samples) {
  const labels = [
    'Worst placement (lowest score)',
    'Median placement (middle of all trials)',
    'Best placement (highest score)',
  ];

  return samples.map((sample, i) => ({
    label: labels[i],
    score: sample.score,
    url: renderPlacementPreview(envImg, patternImg, sample.placement),
  }));
}

export function getEffectivenessTier(score) {
  if (score >= 85) return { label: 'Excellent', className: 'tier-excellent' };
  if (score >= 70) return { label: 'Good', className: 'tier-good' };
  if (score >= 55) return { label: 'Fair', className: 'tier-fair' };
  return { label: 'Developing', className: 'tier-developing' };
}

export function getMetricMeta() {
  return METRIC_META;
}

export async function scoreCamoEffectiveness(environmentImageUrl, patternImageUrl) {
  const [envImg, patternImg] = await Promise.all([
    loadImage(environmentImageUrl),
    loadImage(patternImageUrl),
  ]);

  const envData = sampleImageData(envImg);
  const patternData = sampleImageData(patternImg);
  const regionWeights = computeRegionWeights(envData);

  const placementRng = mulberry32(hashString(environmentImageUrl));
  const placements = generatePlacements(envData.width, envData.height, regionWeights, placementRng);

  const envPixels = collectWeightedLabPixels(envData, regionWeights);
  const patternPixels = extractLabPixels(patternData);

  const placementResult = scorePlacementRobustness(envData, patternData, placements);
  const distanceResult = scoreDistanceSimulation(envData, patternData, regionWeights);
  const lightingResult = scoreLightingRobustness(envPixels, patternPixels);
  const seasonalResult = scoreSeasonalDrift(envPixels, patternPixels);
  const saliencyScore = scoreSaliencyStealth(envData, patternData, placements);

  const metrics = {
    colorHarmony: scoreColorHarmony(envPixels, patternPixels),
    textureMatch: scoreTextureMatch(envData, patternData, regionWeights),
    luminanceBalance: scoreLuminanceBalance(envPixels, patternPixels),
    outlineDisruption: scoreOutlineDisruption(envData, patternData, regionWeights),
    placementRobustness: placementResult.score,
    distanceSimulation: distanceResult.score,
    lightingRobustness: lightingResult.score,
    saliencyStealth: saliencyScore,
    scaleMatching: scoreScaleMatching(envData, patternData, regionWeights),
    seasonalDrift: seasonalResult.score,
  };

  const overall = Math.round(
    Object.entries(METRIC_WEIGHTS).reduce((sum, [key, weight]) => sum + metrics[key] * weight, 0)
  );

  const relevantCoverage = Array.from(regionWeights).filter((w) => w >= 0.5).length / regionWeights.length;

  return {
    overall,
    tier: getEffectivenessTier(overall),
    metrics,
    metricDetails: Object.keys(metrics).map((key) => ({
      key,
      score: Math.round(metrics[key]),
      weight: METRIC_WEIGHTS[key],
      ...METRIC_META[key],
    })).sort((a, b) => b.weight - a.weight),
    placementAnalysis: {
      trialCount: placementResult.trialCount,
      median: placementResult.median,
      worstCase: placementResult.worstCase,
      bestCase: placementResult.bestCase,
      allPreviews: createAllPlacementPreviews(envImg, patternImg, envData, patternData, placementResult.allTrials),
      previews: createMultiPlacementPreviews(envImg, patternImg, placementResult.samples),
      note:
        'The pattern is placed randomly 14 times. Median is the middle score — half of trials scored above it, half below. "Worst" and "Best" show the lowest and highest scoring positions.',
    },
    advancedAnalysis: {
      distance: distanceResult.breakdown,
      lighting: lightingResult.breakdown,
      seasonal: seasonalResult.breakdown,
    },
    regionAnalysis: {
      concealmentCoverage: Math.round(relevantCoverage * 100),
      note:
        relevantCoverage < 0.35
          ? 'Much of this photo appears to be sky or non-terrain — scores focus on the lower, concealment-relevant portion.'
          : 'Scoring emphasizes ground-level terrain and vegetation in your photo.',
    },
    blendPreviewUrl: createPlacementPreviewCollage(envImg, patternImg, placementResult.samples),
    previewThumbnails: {
      environment: createSharpThumbnail(envImg),
      pattern: createSharpThumbnail(patternImg),
    },
    computedAt: new Date().toISOString(),
  };
}
